import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, API_URL } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";

export default function Upload() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("General");
  const [format, setFormat] = useState("long");
  const [tags, setTags] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [thumb, setThumb] = useState("");
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState(250);
  const [rules, setRules] = useState(null);
  const [allowComments, setAllowComments] = useState(true);
  const [allowSharing, setAllowSharing] = useState(true);
  const [allowDownloads, setAllowDownloads] = useState(false);
  const [ageRestricted, setAgeRestricted] = useState(false);
  const [visibility, setVisibility] = useState("public");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    api.get("/categories").then(r=>setCats(r.data.filter(c=>c!=="All")));
    api.get("/me/earnings").then(r=>setRules(r.data.rules));
  }, []);

  if (ready && !user) { nav("/login"); return null; }

  const uploadFile = async (file, kind) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    const { data } = await api.post("/upload/file", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
    });
    return API_URL.replace(/\/api$/, "") + data.url;
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { title, description, thumbnail_url: thumb, video_url: videoUrl,
                        format, category, tags: tags.split(",").map(t=>t.trim()).filter(Boolean),
                        hashtags: hashtags.split(/[,\s]+/).map(t=>t.replace(/^#/,"").trim()).filter(Boolean),
                        visibility, is_paid: isPaid, price_coins: isPaid ? Number(price) : 0,
                        allow_comments: allowComments, allow_sharing: allowSharing,
                        allow_downloads: allowDownloads, age_restricted: ageRestricted };
      if (!payload.video_url) { toast.error("Video URL required"); setBusy(false); return; }
      const { data } = await api.post("/videos", payload);
      toast.success("Uploaded! Publishing now.");
      nav(`/watch/${data.id}`);
    } catch(e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const onFile = async (f, kind) => {
    if (!f) return;
    try {
      setBusy(true); setProgress(0);
      const url = await uploadFile(f, kind);
      if (kind === "video") setVideoUrl(url); else setThumb(url);
      toast.success(kind === "video" ? "Video uploaded" : "Thumbnail uploaded");
    } catch(e){toast.error(apiErr(e));}
    finally { setBusy(false); setProgress(0); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-6">
      <h1 className="h-font text-3xl font-bold">Upload to Jixxeo</h1>
      <p className="text-white/60 mt-1">Your video publishes automatically after processing.</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="upload-form">
        <div className="flex gap-2">
          {["long","short"].map(f => (
            <button key={f} type="button" onClick={()=>setFormat(f)} data-testid={`format-${f}`}
              className={`chip jx-btn px-4 h-10 rounded-full text-sm font-semibold ${format===f?'chip-active':''}`}>
              {f==="long"?"Long-form":"Loop"}
            </button>
          ))}
        </div>
        <div className="border border-dashed border-white/15 rounded-2xl p-6 text-center">
          <UploadCloud size={28} className="mx-auto text-[#08D9D6]"/>
          <label className="block mt-2 text-sm text-white/70">Upload video file</label>
          <input type="file" accept="video/*" data-testid="upload-video-file" onChange={e=>onFile(e.target.files?.[0], "video")}
                 className="mt-2 w-full text-sm"/>
          {busy && progress > 0 && (
            <div className="w-full h-1 bg-white/10 rounded-full mt-3 overflow-hidden">
              <div className="h-full bg-[#08D9D6]" style={{width: progress + "%"}}/>
            </div>
          )}
          <div className="text-xs text-white/50 mt-2">or paste MP4/HLS URL below</div>
          <input value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} placeholder="https://…/video.mp4"
                 data-testid="upload-video-url"
                 className="mt-2 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 text-sm focus:outline-none focus:border-[#08D9D6]"/>
        </div>
        <input value={title} onChange={e=>setTitle(e.target.value)} required placeholder="Title" data-testid="upload-title"
               className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:border-[#08D9D6]"/>
        <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Description" rows={3} data-testid="upload-description"
                  className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 focus:outline-none focus:border-[#08D9D6]"/>
        <div className="grid grid-cols-2 gap-3">
          <select value={category} onChange={e=>setCategory(e.target.value)} data-testid="upload-category"
                  className="h-11 rounded-xl bg-white/5 border border-white/10 px-3 focus:outline-none focus:border-[#08D9D6]">
            {cats.map(c=><option key={c} value={c} className="bg-black">{c}</option>)}
          </select>
          <select value={visibility} onChange={e=>setVisibility(e.target.value)} data-testid="upload-visibility"
                  className="h-11 rounded-xl bg-white/5 border border-white/10 px-3 focus:outline-none focus:border-[#08D9D6]">
            <option className="bg-black" value="public">Public</option>
            <option className="bg-black" value="unlisted">Unlisted</option>
            <option className="bg-black" value="private">Private</option>
          </select>
        </div>
        <input value={tags} onChange={e=>setTags(e.target.value)} placeholder="Tags (comma separated)" data-testid="upload-tags"
               className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:border-[#08D9D6]"/>
        <input value={hashtags} onChange={e=>setHashtags(e.target.value)} placeholder="#hashtags (space or comma separated)" data-testid="upload-hashtags"
               className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:border-[#08D9D6]"/>

        <div className="rounded-2xl border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">Monetization</div>
              <div className="text-xs text-white/60">Charge coins to unlock this video</div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isPaid} onChange={e=>setIsPaid(e.target.checked)} data-testid="upload-paid" className="w-5 h-5 accent-[#FFD700]"/>
              <span className="text-sm font-semibold text-[#FFD700]">Paid video</span>
            </label>
          </div>
          {isPaid && (
            <div className="mt-3">
              <label className="text-xs text-white/60">Price in coins {rules && <>({rules.min_video_price_coins}–{rules.max_video_price_coins})</>}</label>
              <input type="number" value={price} onChange={e=>setPrice(e.target.value)} data-testid="upload-price"
                     min={rules?.min_video_price_coins||50} max={rules?.max_video_price_coins||50000}
                     className="mt-1 w-full h-11 rounded-xl bg-white/5 border border-[#FFD700]/40 px-4 focus:outline-none focus:border-[#FFD700] text-[#FFD700] font-bold"/>
              {rules && <div className="text-[11px] text-white/50 mt-1">≈ ${(price * rules.coin_to_usd).toFixed(2)} USD · platform fee {rules.platform_fee_pct}%</div>}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 p-4 space-y-2">
          <div className="font-semibold text-sm mb-1">Options</div>
          <Toggle label="Allow comments" checked={allowComments} onChange={setAllowComments} testid="opt-comments"/>
          <Toggle label="Allow sharing" checked={allowSharing} onChange={setAllowSharing} testid="opt-sharing"/>
          <Toggle label="Allow downloads (if entitled)" checked={allowDownloads} onChange={setAllowDownloads} testid="opt-downloads"/>
          <Toggle label="Age restricted (18+)" checked={ageRestricted} onChange={setAgeRestricted} testid="opt-age"/>
        </div>

        <div>
          <label className="text-sm text-white/70">Thumbnail (optional — auto-generated if empty)</label>
          <input type="file" accept="image/*" data-testid="upload-thumbnail" onChange={e=>onFile(e.target.files?.[0], "thumbnail")}
                 className="mt-1 w-full text-sm"/>
          {thumb && <img src={thumb} alt="" className="mt-2 rounded-lg max-h-32"/>}
        </div>

        <button disabled={busy} data-testid="upload-submit" type="submit"
                className="jx-btn w-full h-12 rounded-xl bg-[#08D9D6] text-black font-bold disabled:opacity-50 hover:bg-[#06B2B0] inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin"/>} {busy ? "Working…" : "Publish"}
        </button>
      </form>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm">{label}</span>
      <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} data-testid={testid} className="w-5 h-5 accent-[#08D9D6]"/>
    </label>
  );
}
